/**
 * RollScroller Component
 * 
 * A typewriter-style text animation that cycles through a list of roles.
 * The animation types out each role character by character, pauses briefly,
 * then backspaces to delete it before typing the next role in the list.
 * This creates an engaging loop that showcases the many hats a small business
 * owner wears within their company, emphasizing that they shouldn't need to
 * be web developers too.
 */
'use client';

import { useEffect, useState, useCallback } from 'react';

const TYPING_SPEED = 80;      // ms per character when typing
const DELETING_SPEED = 50;    // ms per character when deleting
const PAUSE_AFTER_TYPING = 3000;  // ms to pause after fully typed
const PAUSE_AFTER_DELETING = 500; // ms to pause after fully deleted

interface RollScrollerProps {
    roles?: string[];
}

export default function RollScroller({ 
    roles = [
        'an Accountant',
        'a Marketer',
        'a Salesperson',
        'a Customer Service Rep',
        'an Operations Manager',
        'an HR Manager',
        'a Bookkeeper',
        'a Social Media Manager',
        'a Content Creator',
        'an Office Manager',
        'a Financial Manager',
        'a Marketing Coordinator',
        'a Sales Representative',
        'a Customer Success',
        'an Operations Coordinator',
        'an Inventory Manager',
        'a Project Manager'
    ]
}: RollScrollerProps) {
    const [roleIndex, setRoleIndex] = useState(0);
    const [displayText, setDisplayText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const currentRole = roles[roleIndex];

    const tick = useCallback(() => {
        if (isDeleting) {
            // Remove one character
            setDisplayText(prev => prev.slice(0, -1));
        } else {
            // Add one character
            setDisplayText(currentRole.slice(0, displayText.length + 1));
        }
    }, [isDeleting, currentRole, displayText.length]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (!isDeleting && displayText === currentRole) {
            // Finished typing - pause then start deleting
            timeout = setTimeout(() => setIsDeleting(true), PAUSE_AFTER_TYPING);
        } else if (isDeleting && displayText === '') {
            // Finished deleting - pause, move to next role, then start typing
            timeout = setTimeout(() => {
                setIsDeleting(false);
                setRoleIndex((prev) => (prev + 1) % roles.length);
            }, PAUSE_AFTER_DELETING);
        } else {
            // Continue typing or deleting
            const speed = isDeleting ? DELETING_SPEED : TYPING_SPEED;
            timeout = setTimeout(tick, speed);
        }

        return () => clearTimeout(timeout);
    }, [displayText, isDeleting, currentRole, roles.length, tick]);

    return (
        <span className="inline-block text-black">
            {displayText}
            <span className="text-gray-500 animate-pulse">|</span>
        </span>
    );
}